import type { ResourceTypeHandle } from '@toolproof-core/genesis/_contract/_index';

import {
    defineConnectorConstellationDerivation,
    defineEntityConstellationDerivation,
} from '@toolproof-core/visualization/derivation/_pure/derivation-values';
import type { EntityItemCollection, ItemCollection } from '@toolproof-core/visualization/_contract/collection';
import type { EntityConstellation } from '@toolproof-core/visualization/_contract/constellation';
import type {
    EntityItem,
} from '@toolproof-core/visualization/_contract/item';
import type { CollectionKey, ConstellationKey } from '@toolproof-core/visualization/_contract/key';
import type {
    ConnectorConstellationDerivation,
    ConstellationDerivation,
    EntityConstellationDerivation,
    EntityItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_contract/derivation';

import {
    resolveToolStepLayoutConfig,
    resolveWorkflowStateEntriesConfig,
} from '@/workflow/_pure/config/derivationConfig';
import {
    WORKFLOW_KEYS,
    buildCaseConstellationKey,
    buildMacroStepConnectorConstellationKey,
    buildResourceTypeRadialConnectorConstellationKey,
    buildRoleConstellationKey,
    buildToolItemKey,
    buildToolStepItemKey,
    buildToolRoleConnectorConstellationKey,
    buildWorkflowStateEntryCollectionKey,
    buildWorkflowStateEntryConnectorConstellationKey,
    buildWorkflowStateEntryConstellationKey,
    parseCaseCollectionKey,
    parseMacroStepConnectorCollectionKey,
    parseResourceTypeRadialConnectorCollectionKey,
    parseRoleCollectionKey,
    parseRoleConstellationKey,
    parseToolRoleConnectorCollectionKey,
    parseToolStepConstellationKey,
    parseWorkflowStateEntryConnectorCollectionKey,
    parseWorkflowStateEntryConstellationKey,
    type WorkflowKeyScope,
} from '@/workflow/_pure/keyCodec';
import { normalizePlanarDirection } from '@/workflow/_pure/derivation/geometry';
import { deriveResourceTypeAngularPlacement } from '@/workflow/_pure/derivation/resourceTypeAngularPlacement';
import type { BaseConfig } from '@/workflow/_contract/config';
import type {
    CaseMetadata,
    WorkflowStateEntryCollectionSeed as KernelWorkflowStateEntryCollectionSeed,
} from '@/workflow/_contract/kernel';
import type {
    ToolStepLayoutConfig,
    WorkflowStateEntriesConfig,
} from '@/workflow/_pure/config/derivationConfig';

type CaseConstellationMetadata = {
    ownerMacroStepKey: string;
};

type WorkflowStateEntryCollectionSeed = KernelWorkflowStateEntryCollectionSeed;

export function buildToolStepConstellationDerivation(args: {
    constellationKey: ConstellationKey<'entity'>;
    toolStepCollection: ItemCollection<'entity'>;
    firstToolStepItemKey: EntityItem['itemKey'];
    startRadius: number;
    ringGap: number;
    ringRadii: number[];
    tubeRadii: number[];
    toolStepLayoutConfig: ToolStepLayoutConfig;
    caseMemberships?: readonly CaseMetadata[];
}): EntityConstellationDerivation {
    const slotBands = args.toolStepLayoutConfig.slotBands;
    if (typeof slotBands.enabled !== 'boolean' || !Number.isFinite(slotBands.width)
        || !Number.isFinite(slotBands.thickness) || slotBands.thickness <= 0 || slotBands.width < slotBands.thickness) {
        throw new Error('Workflow toolSteps.slotBands requires an enabled boolean and finite positive thickness <= width.');
    }
    const variant = slotBands.variant ?? 'flat';
    if (variant !== 'flat' && variant !== 'serrated') {
        throw new Error(`Unsupported workflow toolSteps.slotBands.variant: ${variant}`);
    }
    const teeth = slotBands.teeth;
    if (variant === 'serrated' && (teeth === undefined || !Number.isInteger(teeth.count) || teeth.count < 3
        || !Number.isFinite(teeth.depth) || teeth.depth <= 0)) {
        throw new Error('Workflow toolSteps.slotBands.teeth requires an integer count >= 3 and a positive depth when serrated.');
    }
    const whenKeys = new Set(args.caseMemberships?.map((membership) => buildToolStepItemKey(membership.whenToolStepKey)));
    const whatKeys = new Set(args.caseMemberships?.map((membership) => buildToolStepItemKey(membership.whatToolStepKey)));
    const constellation: EntityConstellation = {
        constellationKind: 'entity',
        constellationKey: args.constellationKey,
        itemCollectionKey: args.toolStepCollection.itemCollectionKey,
        anchor: {
            itemKey: args.firstToolStepItemKey,
            anchorKey: 'center',
        },
    };

    return defineEntityConstellationDerivation({
        constellation,
        parametricState: {
            layout: {
                layoutKind: 'concentric-ring',
                anchor: { anchorKind: 'origin' },
                startRadius: args.startRadius,
                ringGap: args.ringGap,
                ringCount: args.toolStepCollection.items.length,
                ...(args.ringRadii.length === args.toolStepCollection.items.length
                    ? { ringRadii: args.ringRadii }
                    : {}),
                ringTube: {
                    tubeRadius: args.toolStepLayoutConfig.tubeRadius,
                    ...(args.tubeRadii.length === args.toolStepCollection.items.length
                        ? { tubeRadii: args.tubeRadii }
                        : {}),
                    radialSegments: args.toolStepLayoutConfig.radialSegments,
                    tubularSegments: args.toolStepLayoutConfig.tubularSegments,
                    ...(slotBands.enabled ? {
                        bandWidth: slotBands.width,
                        bandThickness: slotBands.thickness,
                        ...(variant === 'serrated' && teeth ? { bandTeeth: { count: teeth.count, depth: teeth.depth,
                            profilesByRing: args.toolStepCollection.items.map(item => whatKeys.has(item.itemKey)
                                ? { profile: 'scalloped' as const, count: Math.max(3, Math.round(teeth.count / 4)) }
                                : { profile: 'serrated' as const, count: teeth.count }),
                        } } : {}),
                        crossSectionsByRing: args.toolStepCollection.items.map((item) => {
                            if (whenKeys.has(item.itemKey) && whatKeys.has(item.itemKey)) {
                                throw new Error(`ToolStep cannot be both WHEN and WHAT: ${item.itemKey}`);
                            }
                            return whenKeys.has(item.itemKey) || whatKeys.has(item.itemKey)
                                ? 'horizontal-band' as const : 'tube' as const;
                        }),
                    } : {}),
                },
            },
        },
    });
}

export function buildWorkflowStateEntryConstellationDerivations(args: {
    seeds: WorkflowStateEntryCollectionSeed[];
    workflowStateEntriesConfig: WorkflowStateEntriesConfig;
    scope: WorkflowKeyScope;
}): EntityConstellationDerivation[] {
    return args.seeds.flatMap((seed) => {
        const anchorItemDerivation = seed.itemDerivations[0];
        if (!anchorItemDerivation) return [];

        const itemCollectionKey = buildWorkflowStateEntryCollectionKey({
            ...args.scope,
            stepKey: seed.stepKey,
            resourceTypeHandle: seed.resourceTypeHandle,
            direction: seed.direction,
        });

        const outward = normalizePlanarDirection([Math.cos(seed.angle), 0, Math.sin(seed.angle)]);
        const direction: [number, number, number] = seed.direction === 'input'
            ? [-outward[0], 0, -outward[2]]
            : outward;
        const radialOffset = seed.direction === 'input'
            ? -seed.radialOffset
            : seed.radialOffset;

        const constellation: EntityConstellation = {
            constellationKind: 'entity' as const,
            constellationKey: buildWorkflowStateEntryConstellationKey({
                ...args.scope,
                stepKey: seed.stepKey,
                resourceTypeHandle: seed.resourceTypeHandle,
                direction: seed.direction,
            }),
            itemCollectionKey,
            anchor: {
                itemKey: anchorItemDerivation.item.itemKey,
                anchorKey: 'center' as const,
            },
        };

        return [defineEntityConstellationDerivation({
            constellation,
            parametricState: {
                layout: {
                    layoutKind: 'spoke' as const,
                    anchor: {
                        anchorKind: 'parametric' as const,
                        parametricKind: 'ring-intersection' as const,
                        ringItemKey: seed.stepItemKey,
                        anglePolicy: {
                            anglePolicyKind: 'explicit' as const,
                            angle: seed.angle,
                        },
                        radialOffset,
                    },
                    direction,
                    spacing: seed.radialOffset,
                    ...(seed.itemOffsets.length > 0 ? { itemOffsets: seed.itemOffsets } : {}),
                },
            },
        })];
    });
}

export function buildCaseConstellationDerivations(args: {
    caseItemCollectionDerivations: readonly EntityItemCollectionDerivation[];
    caseMetadataByItemKey: ReadonlyMap<EntityItem['itemKey'], CaseConstellationMetadata>;
}): EntityConstellationDerivation[] {
    const { caseItemCollectionDerivations, caseMetadataByItemKey } = args;

    return caseItemCollectionDerivations
        .flatMap((collectionDerivation) => {
            const firstItemDerivation = collectionDerivation.itemDerivations[0];
            if (!firstItemDerivation) return [];

            const firstMetadata = caseMetadataByItemKey.get(firstItemDerivation.item.itemKey);
            if (!firstMetadata) return [];

            const parsedCollection = parseCaseCollectionKey(collectionDerivation.itemCollectionKey);
            if (!parsedCollection) return [];

            const constellation: EntityConstellation = {
                constellationKind: 'entity' as const,
                constellationKey: buildCaseConstellationKey({
                    workflowHandle: parsedCollection.workflowHandle,
                    threadIndex: parsedCollection.threadIndex,
                    ownerMacroStepKey: firstMetadata.ownerMacroStepKey,
                }),
                itemCollectionKey: collectionDerivation.itemCollectionKey,
                anchor: {
                    itemKey: firstItemDerivation.item.itemKey,
                    anchorKey: 'center' as const,
                },
            };

            return [defineEntityConstellationDerivation({
                constellation,
                // Annular-band items carry their own radii, so the concentric-ring metrics below are never read.
                parametricState: {
                    layout: {
                        layoutKind: 'concentric-ring' as const,
                        anchor: { anchorKind: 'origin' as const },
                        startRadius: 0,
                        ringGap: 0.0001,
                        ringTube: {
                            tubeRadius: 0.01,
                            radialSegments: 8,
                            tubularSegments: 48,
                        },
                    },
                },
            })];
        });
}

export function buildMacroStepConnectorConstellationDerivations(args: {
    itemCollections: ItemCollection[];
}): ConnectorConstellationDerivation[] {
    return args.itemCollections
        .filter(
            (itemCollection): itemCollection is ItemCollection<'connector'> =>
                itemCollection.itemCollectionKind === 'connector'
                && parseMacroStepConnectorCollectionKey(itemCollection.itemCollectionKey) !== null
        )
        .flatMap((itemCollection) => {
            const parsed = parseMacroStepConnectorCollectionKey(itemCollection.itemCollectionKey);
            if (!parsed) return [];

            return [defineConnectorConstellationDerivation({
                constellation: {
                    constellationKind: 'connector' as const,
                    constellationKey: buildMacroStepConnectorConstellationKey({
                        workflowHandle: parsed.workflowHandle,
                        threadIndex: parsed.threadIndex,
                    }),
                    itemCollectionKey: itemCollection.itemCollectionKey,
                },
            })];
        });
}

export function buildWorkflowStateEntryConnectorConstellationDerivations(args: {
    itemCollections: ItemCollection[];
}): ConnectorConstellationDerivation[] {
    return args.itemCollections
        .filter(
            (itemCollection): itemCollection is ItemCollection<'connector'> =>
                itemCollection.itemCollectionKind === 'connector'
                && parseWorkflowStateEntryConnectorCollectionKey(itemCollection.itemCollectionKey) !== null
        )
        .flatMap((itemCollection) => {
            const parsed = parseWorkflowStateEntryConnectorCollectionKey(itemCollection.itemCollectionKey);
            if (!parsed) return [];

            return [defineConnectorConstellationDerivation({
                constellation: {
                    constellationKind: 'connector' as const,
                    constellationKey: buildWorkflowStateEntryConnectorConstellationKey({
                        workflowHandle: parsed.workflowHandle,
                        threadIndex: parsed.threadIndex,
                        resourceTypeHandle: parsed.resourceTypeHandle,
                    }),
                    itemCollectionKey: itemCollection.itemCollectionKey,
                },
            })];
        });
}

export function deriveConstellationDerivations(args: {
    itemCollections: ItemCollection[];
    config: BaseConfig;
    toolResourceTypeHandle: ResourceTypeHandle;
    resourceTypeResourceTypeHandle: ResourceTypeHandle;
}): ConstellationDerivation[] {
    const {
        itemCollections,
        config,
        toolResourceTypeHandle,
        resourceTypeResourceTypeHandle,
    } = args;

    const toolsCollection = itemCollections.find(
        (itemCollection): itemCollection is EntityItemCollection =>
            itemCollection.itemCollectionKind === 'entity'
            && itemCollection.itemCollectionKey === (`collection:entity:${toolResourceTypeHandle}` as CollectionKey<'entity'>)
    );

    const resourceTypeCollection = itemCollections.find(
        (itemCollection): itemCollection is EntityItemCollection =>
            itemCollection.itemCollectionKind === 'entity'
            && itemCollection.itemCollectionKey === (`collection:entity:${resourceTypeResourceTypeHandle}` as CollectionKey<'entity'>)
    );

    const resourceTypeItemKeyByHandle = new Map<ResourceTypeHandle, EntityItem['itemKey']>(
        (resourceTypeCollection?.items ?? [])
            .map((item) => {
                const keyPrefix = 'item:entity:';
                if (!item.itemKey.startsWith(keyPrefix)) return null;

                const resourceTypeHandle = item.itemKey.slice(keyPrefix.length) as ResourceTypeHandle;
                return [resourceTypeHandle, item.itemKey] as const;
            })
            .filter((entry): entry is readonly [ResourceTypeHandle, EntityItem['itemKey']] => entry !== null)
    );

    const toolConstellations = (() => {
        if (!toolsCollection || toolsCollection.items.length === 0) return [];

        const toolAnchorItem = toolsCollection.items[0];
        if (!toolAnchorItem) return [];

        const toolConstellationConfig = config.domainPresentation.entities.tools;
        const toolLayout = toolConstellationConfig?.layout;
        if (!toolLayout) return [];
        if (toolLayout.anchor !== undefined && toolLayout.anchor.anchorKind !== 'origin') return [];

        const constellation: EntityConstellation = {
            constellationKind: 'entity' as const,
            constellationKey: WORKFLOW_KEYS.constellations.tool,
            itemCollectionKey: toolsCollection.itemCollectionKey,
            anchor: {
                itemKey: toolAnchorItem.itemKey,
                anchorKey: 'center',
            } as const,
        };

        return [defineEntityConstellationDerivation({
            constellation,
            parametricState: {
                layout: {
                    ...toolLayout,
                    anchor: { anchorKind: 'origin' as const },
                },
            },
        })];
    })();

    const resourceTypeConstellations = (() => {
        if (!resourceTypeCollection || resourceTypeCollection.items.length === 0) return [];

        const resourceTypeAnchorItem = resourceTypeCollection.items[0];
        if (!resourceTypeAnchorItem) return [];

        const resourceTypeConstellationConfig =
            config.domainPresentation.entities.resourceTypes;
        const resourceTypeLayout = resourceTypeConstellationConfig?.layout;
        if (!resourceTypeLayout) return [];
        if (resourceTypeLayout.anchor !== undefined && resourceTypeLayout.anchor.anchorKind !== 'origin') return [];

        const constellationKey = WORKFLOW_KEYS.constellations.resourceType;

        const constellation: EntityConstellation = {
            constellationKind: 'entity' as const,
            constellationKey,
            itemCollectionKey: resourceTypeCollection.itemCollectionKey,
            anchor: {
                itemKey: resourceTypeAnchorItem.itemKey,
                anchorKey: 'center',
            } as const,
        };

        return [defineEntityConstellationDerivation({
            constellation,
            parametricState: {
                layout: {
                    layoutKind: 'ring' as const,
                    anchor: { anchorKind: 'origin' as const },
                    radius: resourceTypeLayout.radius,
                    angularPlacement: deriveResourceTypeAngularPlacement({
                        resourceTypeItems: resourceTypeCollection.items,
                        config,
                    }),
                },
            },
        })];
    })();

    const roleConstellations = itemCollections
        .filter(
            (itemCollection): itemCollection is EntityItemCollection =>
                itemCollection.itemCollectionKind === 'entity'
                && parseRoleCollectionKey(itemCollection.itemCollectionKey) !== null
        )
        .flatMap((itemCollection) => {
            const parsed = parseRoleCollectionKey(itemCollection.itemCollectionKey);
            if (!parsed) return [];

            const roleConfig = config.domainPresentation.entities.roles[parsed.direction];
            const anchorItemKey = resourceTypeItemKeyByHandle.get(parsed.resourceTypeHandle as ResourceTypeHandle);
            if (!anchorItemKey) return [];

            const toolItemKey = buildToolItemKey(parsed.toolHandle);

            const constellation: EntityConstellation = {
                constellationKind: 'entity' as const,
                constellationKey: buildRoleConstellationKey({
                    toolHandle: parsed.toolHandle,
                    direction: parsed.direction,
                    resourceTypeHandle: parsed.resourceTypeHandle,
                }),
                itemCollectionKey: itemCollection.itemCollectionKey,
                anchor: {
                    itemKey: anchorItemKey,
                    anchorKey: 'center' as const,
                },
                meta: {
                    ownerToolHandle: parsed.toolHandle,
                    ownerToolItemKey: toolItemKey,
                    roleDirection: parsed.direction,
                    roleResourceTypeHandle: parsed.resourceTypeHandle,
                },
            };

            return [defineEntityConstellationDerivation({
                constellation,
                parametricState: {
                    layout: {
                        layoutKind: 'crescent' as const,
                        anchor: {
                            anchorKind: 'item' as const,
                            anchorItemKey,
                        },
                        radius: roleConfig.layout.radius,
                        arcCenterAngle: roleConfig.layout.arcCenterAngle,
                        ...(roleConfig.layout.equatorGap === undefined
                            ? {}
                            : { equatorGap: roleConfig.layout.equatorGap }),
                        ...(roleConfig.layout.arcSpan === undefined
                            ? {}
                            : { arcSpan: roleConfig.layout.arcSpan }),
                        ...(roleConfig.layout.rotation === undefined
                            ? {}
                            : { rotation: roleConfig.layout.rotation }),
                    },
                },
            })];
        });

    const connectorConstellations = itemCollections
        .filter(
            (itemCollection): itemCollection is ItemCollection<'connector'> =>
                itemCollection.itemCollectionKind === 'connector'
                && parseToolRoleConnectorCollectionKey(itemCollection.itemCollectionKey) !== null
        )
        .flatMap((itemCollection) => {
            const parsed = parseToolRoleConnectorCollectionKey(itemCollection.itemCollectionKey);
            if (!parsed) return [];

            const toolItemKey = buildToolItemKey(parsed.toolHandle);

            return [defineConnectorConstellationDerivation({
                constellation: {
                    constellationKind: 'connector' as const,
                    constellationKey: buildToolRoleConnectorConstellationKey({
                        toolHandle: parsed.toolHandle,
                    }),
                    itemCollectionKey: itemCollection.itemCollectionKey,
                    meta: {
                        ownerToolHandle: parsed.toolHandle,
                        ownerToolItemKey: toolItemKey,
                    },
                },
            })];
        });

    return [
        ...toolConstellations,
        ...resourceTypeConstellations,
        ...roleConstellations,
        ...connectorConstellations,
        ...buildResourceTypeRadialConnectorConstellationDerivations(itemCollections),
    ];
}

function isEntityConstellationDerivation(
    constellationDerivation: ConstellationDerivation,
): constellationDerivation is EntityConstellationDerivation {
    return constellationDerivation.constellation.constellationKind === 'entity';
}

/** Layers config-driven presentation onto the layout each builder already produced. */
export function enrichConstellationDerivations(args: {
    constellationDerivations: readonly ConstellationDerivation[];
    config: BaseConfig;
}): ConstellationDerivation[] {
    const { constellationDerivations, config } = args;

    return constellationDerivations.map((constellationDerivation) => {
        if (!isEntityConstellationDerivation(constellationDerivation)) {
            const { constellation, parametricState } = constellationDerivation;
            const ownerToolItemKey = constellation.meta?.ownerToolItemKey;
            if (typeof ownerToolItemKey !== 'string') return constellationDerivation;

            return defineConnectorConstellationDerivation({
                constellation,
                parametricState: {
                    ...(parametricState ?? {}),
                    visibilityRule: {
                        visibleOnHoverOf: {
                            constellationKey: WORKFLOW_KEYS.constellations.tool,
                            itemKey: ownerToolItemKey as EntityItem['itemKey'],
                        },
                        visibleOnActiveOf: {
                            constellationKey: WORKFLOW_KEYS.constellations.tool,
                            itemKey: ownerToolItemKey as EntityItem['itemKey'],
                        },
                    },
                },
            });
        }

        const { constellation, parametricState } = constellationDerivation;
        const layout = parametricState.layout;
        if (!layout) return constellationDerivation;

        if (constellation.constellationKey === WORKFLOW_KEYS.constellations.tool) {
            const toolConstellationConfig = config.domainPresentation.entities.tools;
            if (!toolConstellationConfig) return constellationDerivation;
            if (layout.layoutKind !== 'sector-ring') return constellationDerivation;

            const { layout: _toolDeclaredLayout, item: _toolItem, ...toolParametricState } = toolConstellationConfig;
            return defineEntityConstellationDerivation({
                constellation,
                parametricState: {
                    ...toolParametricState,
                    layout,
                },
            });
        }

        if (
            constellation.constellationKey === WORKFLOW_KEYS.constellations.resourceType
        ) {
            const resourceTypeConstellationConfig = config.domainPresentation.entities.resourceTypes;
            if (!resourceTypeConstellationConfig) return constellationDerivation;
            if (layout.layoutKind !== 'ring') return constellationDerivation;

            const {
                layout: _resourceTypeDeclaredLayout,
                item: _resourceTypeItem,
                ...resourceTypeParametricState
            } = resourceTypeConstellationConfig;
            return defineEntityConstellationDerivation({
                constellation,
                parametricState: {
                    ...resourceTypeParametricState,
                    layout,
                },
            });
        }

        const roleConstellation = parseRoleConstellationKey(constellation.constellationKey);
        if (roleConstellation) {
            if (layout.layoutKind !== 'crescent') return constellationDerivation;

            const roleConfig = config.domainPresentation.entities.roles[roleConstellation.direction];
            const toolItemKey = buildToolItemKey(roleConstellation.toolHandle);

            const {
                layout: _roleDeclaredLayout,
                item: _roleItem,
                visibilityRule: declaredVisibilityRule,
                ...roleParametricState
            } = roleConfig;

            return defineEntityConstellationDerivation({
                constellation,
                parametricState: {
                    ...roleParametricState,
                    layout,
                    visibilityRule: {
                        visibleOnHoverOf: {
                            constellationKey: WORKFLOW_KEYS.constellations.tool,
                            itemKey: toolItemKey,
                        },
                        visibleOnActiveOf: {
                            constellationKey: WORKFLOW_KEYS.constellations.tool,
                            itemKey: toolItemKey,
                        },
                        ...(declaredVisibilityRule ?? {}),
                    },
                },
            });
        }

        if (parseToolStepConstellationKey(constellation.constellationKey)) {
            if (layout.layoutKind !== 'concentric-ring') return constellationDerivation;

            const toolStepLayoutConfig = resolveToolStepLayoutConfig(config);
            if (toolStepLayoutConfig.labelChannelPolicy === undefined) return constellationDerivation;

            return defineEntityConstellationDerivation({
                constellation,
                parametricState: {
                    layout,
                    labelChannelPolicy: toolStepLayoutConfig.labelChannelPolicy,
                },
            });
        }

        if (parseWorkflowStateEntryConstellationKey(constellation.constellationKey)) {
            if (layout.layoutKind !== 'spoke') return constellationDerivation;

            const workflowStateEntriesConfig = resolveWorkflowStateEntriesConfig(config);
            if (workflowStateEntriesConfig.labelChannelPolicy === undefined) return constellationDerivation;

            return defineEntityConstellationDerivation({
                constellation,
                parametricState: {
                    layout,
                    labelChannelPolicy: workflowStateEntriesConfig.labelChannelPolicy,
                },
            });
        }

        return constellationDerivation;
    });
}

function buildResourceTypeRadialConnectorConstellationDerivations(
    itemCollections: ItemCollection[],
): ConnectorConstellationDerivation[] {
    return itemCollections
        .filter(
            (itemCollection): itemCollection is ItemCollection<'connector'> =>
                itemCollection.itemCollectionKind === 'connector'
                && parseResourceTypeRadialConnectorCollectionKey(itemCollection.itemCollectionKey) !== null
        )
        .flatMap((itemCollection) => {
            const parsed = parseResourceTypeRadialConnectorCollectionKey(itemCollection.itemCollectionKey);
            if (!parsed) return [];

            return [defineConnectorConstellationDerivation({
                constellation: {
                    constellationKind: 'connector' as const,
                    constellationKey: buildResourceTypeRadialConnectorConstellationKey({
                        workflowHandle: parsed.workflowHandle,
                        threadIndex: parsed.threadIndex,
                    }),
                    itemCollectionKey: itemCollection.itemCollectionKey,
                },
            })];
        });
}
